import React, {useEffect, useState} from "react";
import * as axios from "axios";
import {Dialog, IconButton, Button, Popover, TextField} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "@material-ui/icons/Edit";
import ImageLightbox from "./ImageLightbox";
import CreditOrderModal from "./CreditOrderModal";
import "./OrderDetailsModal.css";

const CREDIT_ELIGIBLE_STATUSES = ['canceled', 'delivered'];
// Anything left below this (lei) counts as "fully credited" — same
// tolerance used server-side when comparing floats.
const FULLY_CREDITED_EPSILON = 0.01;

const baseUrl = 'https://api.appetito.ro/';

const RECEIPT_BUCKET_URL = 'https://s3.eu-central-1.amazonaws.com/appetito-orders/all';

// Receipt photos are uploaded by the deliverer app when they pick up the
// order, keyed by the date they were taken (not the order's creation date)
// and the order id, e.g. .../2026/8/16/524292.jpg — month/day aren't
// zero-padded.
function receiptImageUrl(order) {
  const timestampSeconds = order.delivered_at || order.picked_at || order.created;
  if (!timestampSeconds || !order.order_id) {
    return null;
  }
  const date = new Date(timestampSeconds * 1000);
  return `${RECEIPT_BUCKET_URL}/${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}/${order.order_id}.jpg`;
}

// Turda admins also see Câmpia Turzii's restaurants mixed into the same
// dashboard (see the backend's ListAllOrders, which pulls both cities' orders
// once the deliverer's own city is Turda) — this tag is the only thing that
// tells them apart at a glance.
function restaurantDisplayName(order) {
  return order.restaurant_city === 'Câmpia Turzii'
    ? `${order.restaurant_name} C. Turzii`
    : order.restaurant_name;
}

function money(value) {
  return (parseFloat(value) || 0).toFixed(2);
}

function pad(n) {
  return `${n}`.padStart(2, '0');
}

function formatDateTime(timestampSeconds) {
  if (!timestampSeconds) {
    return '';
  }
  const date = new Date(timestampSeconds * 1000);
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function formatTime(timestampSeconds) {
  if (!timestampSeconds) {
    return '';
  }
  const date = new Date(timestampSeconds * 1000);
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// "HH:MM" for <input type="time">: the restaurant's time, or now when it
// hasn't given one yet.
function timeInputValue(timestampSeconds) {
  const date = timestampSeconds > 0 ? new Date(timestampSeconds * 1000) : new Date();
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// One info row that can switch to an input. Saving only happens on the
// button: on iOS Safari the time wheel fires onChange after the hour moves,
// before the minutes, so it can't save on change.
function EditableRow({label, value, inputType = 'text', initialInput, onSave, inputProps}) {
  const [editing, setEditing] = useState(false);
  const [input, setInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const start = () => {
    setInput(initialInput);
    setError('');
    setEditing(true);
  };

  const save = async () => {
    if (!input.trim()) {
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave(input.trim());
      setEditing(false);
    } catch (e) {
      setError('Eroare la salvare.');
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="order-modal-info-row">
        <span className="order-modal-info-label">{label}</span>
        <span className="order-modal-editable-value">
          {value}
          <IconButton size="small" title={`Editează ${label.toLowerCase()}`} onClick={start}>
            <EditIcon fontSize="small"/>
          </IconButton>
        </span>
      </div>
    );
  }
  return (
    <div className="order-modal-info-row order-modal-edit-row">
      <span className="order-modal-info-label">{label}</span>
      <div className="order-modal-edit">
        <TextField
          type={inputType}
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={(event) => event.key === 'Enter' && save()}
          margin="dense"
          autoFocus
          fullWidth={inputType !== 'time'}
          inputProps={inputProps}
        />
        <div className="order-modal-edit-actions">
          <Button size="small" onClick={() => setEditing(false)} disabled={saving}>Anulează</Button>
          <Button size="small" variant="contained" color="primary" onClick={save} disabled={saving || !input.trim()}>
            Salvează
          </Button>
        </div>
        {error && <div className="order-modal-edit-error">{error}</div>}
      </div>
    </div>
  );
}

// Flattens each product into a product row plus one row per extra (and one
// for the package fee, if any) so the table can show quantity / unit price /
// row total uniformly for every priced line of the order.
export function buildRows(products) {
  const rows = [];
  (products || []).forEach((product) => {
    rows.push({
      key: `p${product.product_id}-${rows.length}`,
      name: product.name,
      quantity: product.quantity,
      unitPrice: product.price,
      total: product.quantity * product.price,
      isExtra: false,
    });
    (product.extra || []).forEach((extra, index) => {
      rows.push({
        key: `e${extra.extra_id}-${index}-${rows.length}`,
        name: extra.name,
        quantity: product.quantity,
        unitPrice: extra.price,
        total: product.quantity * extra.price,
        isExtra: true,
      });
    });
    if (product.package_fee) {
      rows.push({
        key: `pkg-${rows.length}`,
        name: 'Ambalaj',
        quantity: product.quantity,
        unitPrice: product.package_fee,
        total: product.quantity * product.package_fee,
        isExtra: true,
      });
    }
  });
  return rows;
}

// `onChanged`: called after an edit is saved, so the parent can refresh its list.
export default function OrderDetailsModal({order: orderProp, onClose, onChanged}) {
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [creditModalOpen, setCreditModalOpen] = useState(false);
  const [creditInfoAnchor, setCreditInfoAnchor] = useState(null);
  // Seeded from the order prop, then overwritten with whatever
  // CreditOrderModal's POST response comes back with — order itself is
  // owned by the parent list/board state, so a successful credit updates
  // the button/info display in place without waiting on a refetch.
  const [creditOverride, setCreditOverride] = useState(null);
  // Edits saved from here (address, phone, restaurant time), shown in place
  // until the parent's copy of the order catches up.
  const [edits, setEdits] = useState({});
  const order = orderProp && {...orderProp, ...edits};

  // A different order was opened (or the modal closed) — any override from
  // the previous one no longer applies.
  useEffect(() => {
    setCreditModalOpen(false);
    setCreditInfoAnchor(null);
    setCreditOverride(null);
    setEdits({});
  }, [orderProp?.order_id]);

  const saveField = async (key, value, shown) => {
    const token = localStorage.getItem('access_token');
    const response = await axios.default.put(`${baseUrl}v1/admin/orders/${order.order_id}`, {[key]: value}, {
      headers: {Authorization: `Bearer ${token}`},
    });
    if (response.data && response.data.success === false) {
      throw new Error('not saved');
    }
    setEdits((current) => ({...current, ...shown}));
    if (onChanged) {
      onChanged();
    }
  };

  const saveRestaurantTime = (value) => {
    const [hours, minutes] = value.split(':').map(Number);
    const date = order.restaurant_eta > 0 ? new Date(order.restaurant_eta * 1000) : new Date();
    date.setHours(hours, minutes, 0, 0);
    return saveField('restaurant_eta', date, {restaurant_eta: Math.floor(date.getTime() / 1000)});
  };

  if (!order) {
    return null;
  }

  const credits = creditOverride?.credits ?? order.credits ?? [];
  const creditedTotal = creditOverride?.credited_total ?? order.credited_total ?? 0;

  const rows = buildRows(order.products);
  const subtotal = rows.reduce((sum, row) => sum + row.total, 0);
  const appTax = parseFloat(order.app_tax) || 0;
  const transportPrice = (parseFloat(order.transport_price) || 0) - appTax;
  const voucher = parseFloat(order.voucher) || 0;
  // Web checkout extras, stored separately on the order and not included in
  // total_price (see the backend's ListOrders).
  const priorityFee = parseFloat(order.priority_fee) || 0;
  const minimumOrderFee = parseFloat(order.minimum_order_fee) || 0;
  const tip = parseFloat(order.tip) || 0;
  const orderTotal = (parseFloat(order.total_price) || 0) + priorityFee + minimumOrderFee + tip;
  const hasDeliverer = !!(order.deliverer && order.deliverer.trim());
  const receiptUrl = ['picked', 'delivered'].includes(order.status) ? receiptImageUrl(order) : null;
  const isCreditEligible = order.paid && CREDIT_ELIGIBLE_STATUSES.includes(order.status);
  const remainingToCredit = (parseFloat(order.total_price) || 0) - creditedTotal;
  const hasCredits = credits.length > 0;
  // 'admin' in localStorage is populated from the /admin/super-admin login
  // check, so this is really a superadmin gate despite the key's name.
  const isSuperAdmin = localStorage.getItem('admin') === 'true';
  const canCreditMore = isSuperAdmin && isCreditEligible && remainingToCredit > FULLY_CREDITED_EPSILON;

  const closeAll = () => {
    setReceiptOpen(false);
    onClose();
  };

  return (
    <Dialog open={!!order} onClose={closeAll} fullWidth maxWidth="sm">
      <div className="order-modal">
        <div className="order-modal-topbar">
          <div className="order-modal-header">
            <span className="order-modal-id">#{order.order_id}</span>
            {order.restaurant_phone ? (
              <a href={`tel:${order.restaurant_phone}`} className="order-modal-restaurant order-modal-restaurant-link">
                {restaurantDisplayName(order)}
              </a>
            ) : (
              <span className="order-modal-restaurant">{restaurantDisplayName(order)}</span>
            )}
          </div>
          <div className="order-modal-topbar-actions">
            {!!receiptUrl && (
              <Button size="small" variant="outlined" onClick={() => setReceiptOpen(true)}>
                Bon
              </Button>
            )}
            {hasCredits && (
              <button
                type="button"
                className="order-modal-credit-pill"
                onClick={(event) => setCreditInfoAnchor(event.currentTarget)}
              >
                Creditată
              </button>
            )}
            {canCreditMore && (
              <Button size="small" variant="outlined" onClick={() => setCreditModalOpen(true)}>
                Creditează
              </Button>
            )}
            <IconButton className="order-modal-close" size="small" onClick={closeAll}>
              <CloseIcon fontSize="small"/>
            </IconButton>
          </div>
        </div>

        <ImageLightbox
          src={receiptOpen ? receiptUrl : null}
          alt={`Bon comandă #${order.order_id}`}
          onClose={() => setReceiptOpen(false)}
        />

        <div className="order-modal-info">
          <div className="order-modal-info-row">
            <span className="order-modal-info-label">Creată</span>
            <span>{formatDateTime(order.created)}</span>
          </div>
          <EditableRow
            label="Ora dată de restaurant"
            value={order.restaurant_eta > 0 ? formatTime(order.restaurant_eta) : '—'}
            inputType="time"
            initialInput={timeInputValue(order.restaurant_eta)}
            inputProps={{step: 60}}
            onSave={saveRestaurantTime}
          />
          {!!order.finished_at && (
            <div className="order-modal-info-row">
              <span className="order-modal-info-label">Pregătită de restaurant</span>
              <span>{formatDateTime(order.finished_at)}</span>
            </div>
          )}
          {!!order.picked_at && (
            <div className="order-modal-info-row">
              <span className="order-modal-info-label">Ridicată de livrator</span>
              <span>{formatDateTime(order.picked_at)}</span>
            </div>
          )}
          {!!order.delivered_at && (
            <div className="order-modal-info-row">
              <span className="order-modal-info-label">Livrată</span>
              <span>{formatDateTime(order.delivered_at)}</span>
            </div>
          )}
          {hasDeliverer && (
            <div className="order-modal-info-row">
              <span className="order-modal-info-label">Livrator</span>
              <span>{order.deliverer}</span>
            </div>
          )}
          {order.delivery ? (
            <EditableRow
              label="Adresă"
              value={order.address}
              initialInput={order.address || ''}
              onSave={(value) => saveField('address_string', value, {address: value})}
            />
          ) : (
            <div className="order-modal-info-row">
              <span className="order-modal-info-label">Adresă</span>
              <span>Ridicare</span>
            </div>
          )}
          {!!order.mentions && (
            <div className="order-modal-info-row">
              <span className="order-modal-info-label">Mențiuni</span>
              <span>{order.mentions}</span>
            </div>
          )}
          <div className="order-modal-info-row">
            <span className="order-modal-info-label">Client</span>
            <span>{order.user_name}</span>
          </div>
          <EditableRow
            label="Telefon"
            value={order.phone
              ? <a href={`tel:${order.phone}`} className="order-modal-phone-link">{order.phone}</a>
              : '—'}
            inputType="tel"
            initialInput={order.phone || ''}
            onSave={(value) => saveField('phone', value, {phone: value})}
          />
          {!!order.user_id && (
            <div className="order-modal-info-row">
              <a href={`/orders?userId=${order.user_id}`} className="order-modal-history-link">
                Arată istoric comenzi
              </a>
            </div>
          )}
          <div className="order-modal-info-row">
            <a href={`/info-client?orderId=${order.order_id}`} className="order-modal-history-link">
              Arată info client
            </a>
          </div>
          <div className="order-modal-info-row">
            <span className="order-modal-info-label">Plată</span>
            <span className={`order-modal-payment-badge ${order.paid ? 'online' : 'cash'}`}>
              {order.paid ? 'Card online' : 'Cash'}
            </span>
          </div>
        </div>

        <table className="order-modal-products">
          <thead>
          <tr>
            <th>Produs</th>
            <th>Cant.</th>
            <th>Preț unitar</th>
            <th>Total</th>
          </tr>
          </thead>
          <tbody>
          {rows.map((row) => (
            <tr key={row.key} className={row.isExtra ? 'order-modal-extra-row' : 'order-modal-product-row'}>
              <td>{row.name}</td>
              <td>{row.quantity}</td>
              <td>{money(row.unitPrice)}</td>
              <td>{money(row.total)}</td>
            </tr>
          ))}
          </tbody>
        </table>

        <div className="order-modal-footer">
          <div className="order-modal-footer-row">
            <span>Subtotal</span>
            <span>{money(subtotal)} lei</span>
          </div>
          <div className="order-modal-footer-row">
            <span>Transport</span>
            <span>{money(transportPrice)} lei</span>
          </div>
          <div className="order-modal-footer-row">
            <span>Taxă aplicație</span>
            <span>{money(appTax)} lei</span>
          </div>
          {priorityFee > 0 && (
            <div className="order-modal-footer-row">
              <span>Livrare prioritară</span>
              <span>{money(priorityFee)} lei</span>
            </div>
          )}
          {minimumOrderFee > 0 && (
            <div className="order-modal-footer-row">
              <span>Comandă minimă</span>
              <span>{money(minimumOrderFee)} lei</span>
            </div>
          )}
          {voucher > 0 && (
            <div className="order-modal-footer-row">
              <span>Voucher</span>
              <span>-{money(voucher)} lei</span>
            </div>
          )}
          {tip > 0 && (
            <div className="order-modal-footer-row">
              <span>Bacșiș curier</span>
              <span>{money(tip)} lei</span>
            </div>
          )}
          {order.wheel_prize && (
            <div className="order-modal-footer-row">
              <span>Premiu Roata</span>
              <span>🎁 {order.wheel_prize}</span>
            </div>
          )}
          <div className="order-modal-footer-row order-modal-total">
            <span>TOTAL</span>
            <span>{money(orderTotal)} lei</span>
          </div>
        </div>
      </div>

      <Popover
        open={!!creditInfoAnchor}
        anchorEl={creditInfoAnchor}
        onClose={() => setCreditInfoAnchor(null)}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
      >
        <div className="order-modal-credit-history">
          {credits.map((credit) => (
            <div key={credit.id} className="order-modal-credit-history-row">
              <span>{credit.admin_name}</span>
              <span>{money(credit.amount)} lei</span>
              <span>{formatDateTime(credit.created)}</span>
            </div>
          ))}
        </div>
      </Popover>

      {creditModalOpen && (
        <CreditOrderModal
          order={{...order, credited_total: creditedTotal}}
          onClose={() => setCreditModalOpen(false)}
          onCredited={(data) => {
            setCreditOverride(data);
            setCreditModalOpen(false);
          }}
        />
      )}
    </Dialog>
  );
}
